import React, { Component } from "react";
import { RECIPE_COLOURS } from "./DoserRecipePane";

const DAY_MINS = 1440;
const BAR_HEIGHT = 36;
const LABEL_HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

const TEXT = "#2c3e50";
const TEXT_MUTED = "#8a9aac";
const BORDER = "#c5ced8";
const BAR_BG = "#eef1f4";

function formatHourLabel(hour) {
  if (hour === 0 || hour === 24) return "12AM";
  if (hour === 12) return "12PM";
  if (hour < 12) return `${hour}AM`;
  return `${hour - 12}PM`;
}

function recipeColourMap(recipes) {
  const map = {};
  (recipes || []).forEach((r, i) => {
    if (!r || !r.recipeId || r.recipeId === "none") return;
    map[r.recipeId] =
      r.color || RECIPE_COLOURS[i % RECIPE_COLOURS.length];
  });
  return map;
}

/** A recipe holds from its start until the next start. The last one of the
 *  day holds through midnight up to the first. Two rows on the same minute:
 *  the later row replaces the earlier one. */
function buildVisibleBlocks(schedule, recipes) {
  const colours = recipeColourMap(recipes);
  const rows = [];
  (schedule || []).forEach((s, i) => {
    if (!s || !s.recipeId || s.recipeId === "none") return;
    const start = Math.round(Number(s.dayMins) || 0);
    rows.push({
      idx: i + 1,
      start: ((start % DAY_MINS) + DAY_MINS) % DAY_MINS,
      color: colours[s.recipeId] || RECIPE_COLOURS[i % RECIPE_COLOURS.length],
    });
  });
  rows.sort((a, b) => a.start - b.start || a.idx - b.idx);
  const uniq = [];
  rows.forEach((r) => {
    if (uniq.length && uniq[uniq.length - 1].start === r.start) uniq[uniq.length - 1] = r;
    else uniq.push(r);
  });
  if (!uniq.length) return [];

  const segments = [];
  const push = (row, from, to) => {
    if (to <= from) return;
    const last = segments[segments.length - 1];
    if (last && last.idx === row.idx && last.end === from) last.end = to;
    else segments.push({ idx: row.idx, start: from, end: to, color: row.color });
  };
  for (let k = 0; k < uniq.length; k++) {
    const row = uniq[k];
    const from = row.start;
    const to = k + 1 < uniq.length ? uniq[k + 1].start : uniq[0].start + DAY_MINS;
    if (to <= DAY_MINS) push(row, from, to);
    else {
      push(row, from, DAY_MINS);
      push(row, 0, to - DAY_MINS);
    }
  }
  return segments;
}

const cardStyle = {
  fontFamily: "system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  background: "#fff",
  border: `1px solid ${BORDER}`,
  borderRadius: 12,
  padding: 16,
  boxSizing: "border-box",
  width: "100%",
};

const headerStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "baseline",
  gap: "6px 14px",
  marginBottom: 14,
};

const titleStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: TEXT,
  margin: 0,
};

const blurbStyle = {
  fontSize: 12,
  color: TEXT_MUTED,
  lineHeight: 1.4,
  flex: "1 1 240px",
  margin: 0,
};

const barWrapStyle = {
  position: "relative",
  width: "100%",
};

const barStyle = {
  position: "relative",
  height: BAR_HEIGHT,
  width: "100%",
  background: BAR_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 4,
  boxSizing: "border-box",
  overflow: "hidden",
};

const labelsRowStyle = {
  position: "relative",
  height: 22,
  marginTop: 4,
  width: "100%",
};

export default class ScheduleGraph extends Component {
  renderHourGrid() {
    const lines = [];
    for (let h = 0; h <= 24; h++) {
      const left = `${(h / 24) * 100}%`;
      const major = h % 3 === 0;
      lines.push(
        <div
          key={`g-${h}`}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left,
            width: major ? 1 : 1,
            background: major ? "#b8c2cc" : "#d9dee5",
            pointerEvents: "none",
          }}
        />
      );
    }
    return lines;
  }

  renderLabels() {
    return LABEL_HOURS.map((h) => {
      const pct = (h / 24) * 100;
      const isEnd = h === 24;
      const isStart = h === 0;
      return (
        <div
          key={`l-${h}`}
          style={{
            position: "absolute",
            top: 0,
            left: `${pct}%`,
            transform: isStart
              ? "translateX(0)"
              : isEnd
                ? "translateX(-100%)"
                : "translateX(-50%)",
            fontSize: 11,
            color: TEXT_MUTED,
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatHourLabel(h)}
        </div>
      );
    });
  }

  renderBlocks(blocks) {
    return blocks.map((b, i) => {
      const leftPct = (b.start / DAY_MINS) * 100;
      const widthPct = ((b.end - b.start) / DAY_MINS) * 100;
      const showLabel = widthPct >= 1.2;
      return (
        <div
          key={`b-${b.idx}-${b.start}-${i}`}
          title={`Row ${b.idx}`}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            background: b.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {showLabel ? b.idx : null}
        </div>
      );
    });
  }

  render() {
    const { schedule, recipes } = this.props;
    const blocks = buildVisibleBlocks(schedule, recipes);

    return (
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>The day</h3>
          <p style={blurbStyle}>
            Each colour is a schedule row, numbered to match the table. It holds
            from its start until the next row programs a new recipe. The last
            one of the day holds through midnight.
          </p>
        </div>

        <div style={barWrapStyle}>
          <div style={barStyle}>
            {this.renderHourGrid()}
            {this.renderBlocks(blocks)}
          </div>
          <div style={labelsRowStyle}>{this.renderLabels()}</div>
        </div>
      </div>
    );
  }
}

export { buildVisibleBlocks, DAY_MINS };
